// ===================================================
// GRAMMAR UTILITIES
// Utilidades para concordancia gramatical en español
// Evita errores como "1 pegatinas" o "2 pegatina"
// ===================================================

/**
 * Parses a problem statement to identify structure (Start, Change, Question)
 * attempting to use the numbers to guide the split.
 * @param {string} statement - The full problem text
 * @param {number} data1 - The first number (Start)
 * @param {number} data2 - The second number (Change)
 * @returns {object} { start, change, question }
 */
function parseProblemStatement(statement, data1, data2) {
    if (!statement) return { start: '', change: '', question: '' };

    // 1. Extract Question
    const questionMatch = statement.match(/[¿]([^?]+)[?]/);
    const question = questionMatch ? questionMatch[0] : '';
    let textBody = statement.replace(/[¿][^?]+[?]/, '').trim();

    // 2. Locate Data 1 and Data 2 in text
    // Convert to string to find index
    const sData1 = data1 !== undefined ? data1.toString() : null;
    const sData2 = data2 !== undefined ? data2.toString() : null;

    if (!sData1 || !sData2) {
        // Fallback to simple split if no data provided
        const parts = textBody.split(/\s+(?:y|pero|,\s*pero|entonces)\s+/);
        return {
            start: parts[0] || '...',
            change: parts[1] || '...',
            question: question || '...'
        };
    }

    // Indices
    const idx1 = textBody.indexOf(sData1);
    const idx2 = textBody.indexOf(sData2, idx1 + sData1.length); // Look for data2 AFTER data1

    if (idx1 === -1 || idx2 === -1) {
        // Fallback if numbers not found exactly
        const parts = textBody.split(/\s+(?:y|pero|,\s*pero|entonces)\s+/);
        return {
            start: parts[0] || '...',
            change: parts[1] || '...',
            question: question || '...'
        };
    }

    // 3. Find split point between idx1 and idx2
    // We look for the LAST period, semicolon, or strong connector between these two indices
    const substringBetween = textBody.substring(idx1 + sData1.length, idx2);

    // Potential split markers in order of preference
    // Period is strongest. Then semicolon. Then "pero", "luego", "más tarde".
    // "y" is weak, can be part of a list.

    let splitRelativeIndex = -1;
    let splitLength = 0; // Length of the separator to remove/skip

    // Check for period
    const lastPeriod = substringBetween.lastIndexOf('.');
    if (lastPeriod !== -1) {
        splitRelativeIndex = lastPeriod;
        splitLength = 1;
    } else {
        // Check for comma + connector or just connector
        // We want the connector that starts the second clause
        const connectors = ['pero', 'luego', 'después', 'más tarde', 'entonces', 'sin embargo'];

        for (const conn of connectors) {
            const idx = substringBetween.toLowerCase().lastIndexOf(conn);
            if (idx !== -1) {
                // If found, this might be it. Use the right-most one.
                if (idx > splitRelativeIndex) {
                    splitRelativeIndex = idx;
                    splitLength = 0; // We keep the connector in the second part usually, or split before it?
                    // User wants "LUEGO / DESPUÉS..." header, so maybe keeping the connector in the text is fine, 
                    // or weird if the connector IS "Luego".
                    // "Al principio... [Texto]". "Luego... [Luego trajeron 5]".
                    // Redundant.
                }
            }
        }

        // If no strong connector, try "y" as last resort if it looks like a clause separator
        if (splitRelativeIndex === -1) {
            const lastY = substringBetween.lastIndexOf(' y ');
            if (lastY !== -1) {
                splitRelativeIndex = lastY;
                splitLength = 0;
            }
        }
    }

    let startText = '';
    let changeText = '';

    if (splitRelativeIndex !== -1) {
        // Absolute index in textBody
        const splitAbsIndex = idx1 + sData1.length + splitRelativeIndex;

        // Part 1: Start to split
        // Include the period if it was a period split?
        // User wants: "Al principio... [Texto]".
        // Usually "Texto" shouldn't end in a trailing connector.

        if (splitLength === 1) { // It was a period
            startText = textBody.substring(0, splitAbsIndex + 1).trim();
            changeText = textBody.substring(splitAbsIndex + 1).trim();
        } else {
            // It was a word connector.
            // Usually we split BEFORE the connector?
            // "Tenía 5 manzanas y llegaron 2". -> "Tenía 5 manzanas" / "y llegaron 2"
            // "Tenía 5 manzanas, pero llegaron 2". -> "Tenía 5 manzanas," / "pero llegaron 2"

            startText = textBody.substring(0, splitAbsIndex).trim();
            changeText = textBody.substring(splitAbsIndex).trim();

            // Cleanup StartText trailing comma
            if (startText.endsWith(',')) startText = startText.slice(0, -1).trim();
        }
    } else {
        // No clear split found between numbers? 
        // Just split halfway or at first space? 
        // This is rare context. fallback to simple split.
        const parts = textBody.split(/\s+(?:y|pero|,\s*pero|entonces)\s+/);
        startText = parts[0] || '';
        changeText = textBody.substring(startText.length).trim();
    }

    // Capitalize safely
    startText = startText ? startText.charAt(0).toUpperCase() + startText.slice(1) : '';
    changeText = changeText ? changeText.charAt(0).toUpperCase() + changeText.slice(1) : '';

    return {
        start: startText,
        change: changeText,
        question: question
    };
}


/**
 * Aplica concordancia numérica correcta en español
 * @param {number} quantity - Cantidad numérica
 * @param {string} singular - Forma singular de la palabra
 * @param {string} plural - Forma plural de la palabra
 * @returns {string} Cantidad + forma correcta de la palabra
 */
function concordar(quantity, singular, plural) {
    if (quantity === 1) {
        return `${quantity} ${singular}`;
    } else {
        return `${quantity} ${plural || singular + 's'}`;
    }
}

/**
 * Obtiene solo la forma correcta (singular o plural) sin la cantidad
 * @param {number} quantity - Cantidad numérica
 * @param {string} singular - Forma singular
 * @param {string} plural - Forma plural
 * @returns {string} Forma correcta de la palabra
 */
function formaCorrecta(quantity, singular, plural) {
    if (quantity === 1) {
        return singular;
    } else {
        return plural || singular + 's';
    }
}

/**
 * Pluraliza automáticamente una palabra según las reglas del español
 * @param {string} word - Palabra en singular
 * @returns {string} Palabra en plural
 */
function pluralizar(word) {
    // Palabras que terminan en vocal (a, e, i, o, u) → añadir 's'
    if (/[aeiou]$/i.test(word)) {
        return word + 's';
    }

    // Palabras que terminan en consonante → añadir 'es'
    // Excepto si termina en 's' y es palabra aguda
    if (word.endsWith('s') || word.endsWith('x')) {
        return word; // Invariable en plural (ej: lunes, crisis)
    }

    // Palabras que terminan en 'z' → cambiar a 'ces'
    if (word.endsWith('z')) {
        return word.slice(0, -1) + 'ces';
    }

    // Caso general de consonante
    return word + 'es';
}

/**
 * Verbo conjugado según la cantidad (concordancia de número)
 * Soporta verbos irregulares y cambios de raíz
 * @param {string|number} quantityOrVerb - Cantidad o el verbo infinitivo (si se usa con subject)
 * @param {string} singularOrSubject - Forma singular o el sujeto
 * @param {string} plural - Forma plural (opcional)
 * @returns {string} Verbo correctamente conjugado
 */
function conjugarVerbo(quantityOrVerb, singularOrSubject, plural) {
    // Si el primer argumento es número, comportamiento legacy (selección)
    if (typeof quantityOrVerb === 'number') {
        return quantityOrVerb === 1 ? singularOrSubject : plural;
    }

    // Si es string, es conjugación real: conjugarVerbo(verbo, sujeto)
    const verb = quantityOrVerb.toLowerCase().trim();
    // El sujeto no se usa mucho en 3a persona pero lo mantenemos por compatibilidad futura
    // Asumimos 3a persona del singular (él/ella/nombre)

    // Mapa de verbos irregulares (3a persona singular)
    const irregulars = {
        // Ser/Estar
        'ser': 'es',
        'estar': 'está',
        // Haber (impersonal)
        'haber': 'hay',
        // Verbos de movimiento y estado
        'ir': 'va',
        'venir': 'viene',
        'llegar': 'llega',
        'salir': 'sale',
        'entrar': 'entra',
        'volver': 'vuelve',
        // Verbos comunes
        'tener': 'tiene',
        'hacer': 'hace',
        'poner': 'pone',
        'poder': 'puede',
        'querer': 'quiere',
        'saber': 'sabe',
        'ver': 've',
        'dar': 'da',
        'decir': 'dice',
        'oír': 'oye',
        'traer': 'trae',
        'caer': 'cae',
        // Cambios de raíz e irregularidades específicas
        'jugar': 'juega',
        'elegir': 'elige',
        'pedir': 'pide',
        'servir': 'sirve',
        'medir': 'mide',
        'repetir': 'repite',
        'seguir': 'sigue',
        'vestir': 'viste',
        'dormir': 'duerme',
        'encontrar': 'encuentra',
        'contar': 'cuenta',
        'recordar': 'recuerda',
        'volar': 'vuela',
        'morder': 'muerde',
        'probar': 'prueba',
        'soñar': 'sueña',
        'costar': 'cuesta',
        'morir': 'muere',
        'empezar': 'empieza',
        'comenzar': 'comienza',
        'merendar': 'merienda',
        'perder': 'pierde',
        'entender': 'entiende',
        'pensar': 'piensa',
        'cerrar': 'cierra',
        'regar': 'riega',
        'nevar': 'nieva',
        'helar': 'hiela'
    };

    if (irregulars[verb]) {
        return irregulars[verb];
    }

    // Conjugación regular
    if (verb.endsWith('ar')) {
        return verb.slice(0, -2) + 'a';
    } else if (verb.endsWith('er')) {
        return verb.slice(0, -2) + 'e';
    } else if (verb.endsWith('ir')) {
        return verb.slice(0, -2) + 'e';
    }

    return verb; // Fallback
}

// Exportar para uso global
if (typeof window !== 'undefined') {
    window.concordar = concordar;
    window.formaCorrecta = formaCorrecta;
    window.pluralizar = pluralizar;
    window.conjugarVerbo = conjugarVerbo;
    window.parseProblemStatement = parseProblemStatement;
}

console.log('✅ [GrammarUtils] Utilidades de gramática cargadas');
